import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Clock, ArrowClockwise } from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";

// Session-timeout banner.
// Reads `sessionExpiresAt` from AuthContext (populated by /auth/me).
// Shows a sticky yellow bar when < 5 minutes remain. Clicking "Stay signed in"
// hits POST /auth/refresh which re-issues the cookie and resets the timer.
// Auto-logs the user out once expiry passes so their next click doesn't hit
// a wall of 401s inside another flow.
const WARN_THRESHOLD_MS = 5 * 60 * 1000; // 5 minutes

export default function SessionTimeoutBanner() {
  const { user, sessionExpiresAt, refreshSession, logout } = useAuth();
  const [now, setNow] = useState(Date.now());
  const [refreshing, setRefreshing] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  // Reset dismissal whenever a fresh session comes in (post-refresh).
  useEffect(() => { setDismissed(false); }, [sessionExpiresAt]);

  // Only tick while a session exists — no need to burn CPU when logged out.
  useEffect(() => {
    if (!sessionExpiresAt || !user) return undefined;
    const t = setInterval(() => setNow(Date.now()), 15_000);
    return () => clearInterval(t);
  }, [sessionExpiresAt, user]);

  if (!user || user === false || !sessionExpiresAt || dismissed) return null;

  const expMs = new Date(sessionExpiresAt).getTime();
  const remainingMs = expMs - now;

  if (remainingMs > WARN_THRESHOLD_MS) return null;

  // Session already expired — best-effort auto-logout so they land on /login.
  if (remainingMs <= 0) {
    (async () => {
      await logout();
      toast.error("Your session ended — please sign in again to continue");
    })();
    return null;
  }

  const minutes = Math.max(0, Math.floor(remainingMs / 60_000));
  const seconds = Math.max(0, Math.floor((remainingMs % 60_000) / 1_000));
  const timeStr = minutes > 0 ? `${minutes} min` : `${seconds} s`;

  const handleStay = async () => {
    setRefreshing(true);
    const ok = await refreshSession();
    setRefreshing(false);
    if (ok) toast.success("Session extended — you're good for another 24 hours");
    else toast.error("Couldn't extend session — please sign in again");
  };

  return (
    <div
      data-testid="session-timeout-banner"
      className="fixed top-0 inset-x-0 z-50 bg-amber-100 border-b border-amber-300 text-amber-900 shadow-sm">
      <div className="max-w-6xl mx-auto flex items-center justify-between gap-3 px-4 py-2">
        <div className="flex items-center gap-2 min-w-0">
          <Clock size={16} weight="fill" className="text-amber-700 shrink-0" />
          <p className="text-xs font-medium truncate">
            <span data-testid="session-timeout-remaining">Your session ends in {timeStr}</span>
            <span className="hidden sm:inline text-amber-800/80"> — save any in-progress work now, or extend below.</span>
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button
            data-testid="session-timeout-stay-btn"
            size="sm" variant="secondary"
            disabled={refreshing}
            onClick={handleStay}
            className="h-7 bg-amber-900 text-white hover:bg-amber-800">
            <ArrowClockwise size={12} className={`mr-1 ${refreshing ? "animate-spin" : ""}`} />
            {refreshing ? "Extending…" : "Stay signed in"}
          </Button>
          <button
            data-testid="session-timeout-dismiss-btn"
            title="Hide until next warning"
            onClick={() => setDismissed(true)}
            className="text-xs text-amber-800 hover:text-amber-900 px-1.5">×</button>
        </div>
      </div>
    </div>
  );
}
